import axios, { AxiosRequestConfig } from "axios";
import { format } from "date-fns";
import sha256 from "sha256";
import { Time } from "../Helper/Time";
import { WeekTypesToWeekTypesNumber } from "../Helper/Week";
import { WeekConditionsType } from "../Helper/WeekConditions";
import { ChargeCategory, ObjectToChargeCategory } from "../Objects/ChargeCategory";
import { ChargePattern, ObjectToChargePattern } from "../Objects/ChargePattern";
import { ObjectToStoreData, StoreData } from "../Objects/StoreInterface";
import { ObjectToStudioData, ObjectToStudioDataProps, StudioData } from "../Objects/StudioInterface";
import { ObjectToStudioPack, PackChargeType, StudioPack } from "../Objects/StudioPack";
import { ObjectToStudioTag, StudioTag } from "../Objects/StudioTagInterface";
import { ObjectToWorkingParticularSetting, WorkingParticularSetting } from "../Objects/WorkingParticularSetting";
import { ObjectToWorkPattern, WorkPattern } from "../Objects/WorkPattern";
import { ObjectToWorkPatternSet, WorkPatternSet } from "../Objects/WorkPatternSet";
import { ApiError } from "./ApiError";

axios.defaults.withCredentials = true;
axios.defaults.xsrfHeaderName = 'X-CSRF-Token';

export const getAllStoreName = async (config?: AxiosRequestConfig): Promise<StoreData[]> => {
    try {

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/get_all_store_name', undefined, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
        return res.data.list.map((data: any) => ObjectToStoreData(data))
    } catch (error) {
        throw error
    }
}

/**
 * getStoreData
 * @returns string
 */
export const getStoreData = async (
    store_id: number, config?: AxiosRequestConfig
): Promise<StoreData> => {
    try {
        let params = new URLSearchParams()
        params.append('str_id', store_id.toString())

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/get_store_data', params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
        return ObjectToStoreData(res.data.data)
    } catch (error) {
        throw error
    }
}

export const getStudioData = async (
    studio_id: number, config?: AxiosRequestConfig
): Promise<StudioData> => {
    try {
        let params = new URLSearchParams()
        params.append('std_id', studio_id.toString())

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/get_studio_data', params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
        return ObjectToStudioData(res.data.data)
    } catch (error) {
        throw error
    }
}

/**
 * getStudioList
 * @param store_id 
 */
export const getStudioList = async (
    store_id: number,
    include_out_of_work?: boolean, config?: AxiosRequestConfig
): Promise<StudioData[]> => {
    try {
        let params = new URLSearchParams()
        params.append('str_id', store_id.toString())
        if( include_out_of_work ) {
            params.append('include_out_of_work', '1')
        }

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/get_studio_list', params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
        return res.data.list.map( (item: ObjectToStudioDataProps) => ObjectToStudioData(item) )
    } catch (error) {
        throw error
    }
}

export interface AddStoreProps {
    store_data: StoreData
}
export const AddStore = async ({
    store_data,
}: AddStoreProps, config?: AxiosRequestConfig): Promise<void> => {
    try {
        let params = new URLSearchParams()
        params.append("str_name", store_data.str_name ?? "")
        params.append("str_login_str", store_data.str_login_str ?? "")
        params.append("str_login_pass", store_data.str_pass ?? "")
        params.append("str_unit", store_data.str_unit ? store_data.str_unit.format("HH:mm:ss") : "");
        params.append("str_open_date", store_data.str_open_date ? format(store_data.str_open_date, "yyyy-MM-dd") : "")

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/add_store',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
        
    } catch (error) {
        throw error
    }
}

export interface EditStoreSettingProps {
    store_data: StoreData
}
export interface EditStoreSettingResults {
    status: string
}
export const EditStoreSetting = async ({
    store_data,
}: EditStoreSettingProps, config?: AxiosRequestConfig): Promise<EditStoreSettingResults> => {
    try {
        if( store_data.str_id === undefined ) {
            throw new ApiError("IDが指定されていません", "")
        }
        let params = new URLSearchParams()
        params.append("str_id", store_data.str_id.toString())
        let set_values = {
            str_name: store_data.str_name,
            str_login_str: store_data.str_login_str,
            str_close_date: store_data?.str_close_date ? format(store_data.str_close_date, "yyyy/MM/dd") : "",
        }
        params.append("set_values", JSON.stringify(set_values))

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/edit_store_setting',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
        return {
            status: res.data.status
        }
    } catch (error) {
        throw error
    }
}

export interface EditStorePasswordProps {
    str_id: number,
    old_str_login_pass: string,
    new_str_login_pass: string,
}
export interface EditStorePasswordResults {}
export const EditStorePassword = async ({
    str_id,
    old_str_login_pass,
    new_str_login_pass,
}: EditStorePasswordProps, config?: AxiosRequestConfig): Promise<EditStorePasswordResults> => {
    try {
        let params = new URLSearchParams()
        params.append("str_id", str_id.toString())
        params.append("old_str_login_pass", sha256(old_str_login_pass))
        params.append("new_str_login_pass", new_str_login_pass)

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/edit_store_password',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
        return {
            status: res.data.status
        }
    } catch (error) {
        throw error
    }
}

export interface EditStudioDataProps {
    studioData: StudioData,
    studioImage?: string,
}
export const EditStudioData = async ({
    studioData,
    studioImage,
}: EditStudioDataProps, config?: AxiosRequestConfig): Promise<void> => {
    try {
        let params = new URLSearchParams()
        if ( !studioData.std_name || !studioData.std_area ) {
            throw new ApiError("スタジオ名もしくは広さが入力されてません", "")
        }
        if ( studioData.str_id ) {
            params.append("str_id", studioData.str_id.toString())
        }
        if ( studioData.std_id ) {
            params.append("std_id", studioData.std_id.toString())
        }
        params.append("std_name", studioData.std_name)
        params.append("std_area", studioData.std_area)
        if ( studioData.sort ) {
            params.append("sort", studioData.sort.toString())
        }
        if (studioData.operation_start_date) {
            params.append("operation_start_date", format(studioData.operation_start_date, "yyyy/MM/dd"))
        }
        if (studioData.operation_end_date) {
            params.append("operation_end_date", format(studioData.operation_end_date, "yyyy/MM/dd"))
        }
        if (studioImage) {
            let str = studioImage.split(",")
            params.append("stdimg", str[1])
        }

        if( studioData.std_id ) {
            let res = await axios.post(
                process.env.REACT_APP_API_ENDPOINT + 'store/edit_studio_data',
                params, config
            )

            if (res.data.status !== 'SUCCESS') {
                throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
            }
        } else {
            let res = await axios.post(
                process.env.REACT_APP_API_ENDPOINT + 'store/add_new_studio',
                params, config
            )

            if (res.data.status !== 'SUCCESS') {
                throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
            }
        }
    } catch (error) {
        throw error
    }

}

export interface getStudioTagListProps {
    str_id: number
}

export interface getStudioTagListResults {
    studioTagList: StudioTag[]
}

export const getStudioTagList = async ({
    str_id
}: getStudioTagListProps, config?: AxiosRequestConfig): Promise<getStudioTagListResults> => {
    try {
        let params = new URLSearchParams()
        params.append("str_id", str_id.toString())

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/get_studio_tag_list',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
        return {
            studioTagList: res.data.list.map( (item: Object) => ObjectToStudioTag(item) )
        }
    } catch (error) {
        throw error
    }
}

export interface regStudioTagDataProps {
    str_id: number,
    stdtag_id: number,
    std_list: number[]
}

export const regStudioTagData = async ({
    str_id,
    stdtag_id,
    std_list
}: regStudioTagDataProps, config?: AxiosRequestConfig): Promise<void> => {
    try {
        let params = new URLSearchParams()
        params.append("str_id", str_id.toString())
        params.append("stdtag_id", stdtag_id.toString())
        params.append("std_list", std_list.join(","))

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/reg_studio_tag_data',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }

    } catch (error) {
        throw error
    }
}

export interface getStoreWorkingPatternListProps {
    str_id: number
}

export interface getStoreWorkingPatternListResult {
    workPatternSetList: WorkPatternSet[]
}
export const getStoreWorkingPatternList = async ({
    str_id,
}: getStoreWorkingPatternListProps, config?: AxiosRequestConfig): Promise<getStoreWorkingPatternListResult> => {
    try {
        let params = new URLSearchParams()
        params.append("str_id", str_id.toString())

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/get_store_working_pattern_list',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
        return {
            workPatternSetList: res.data.list.map( (item: any) => ObjectToWorkPatternSet(item) )
        }
    } catch (error) {
        throw error
    }
}

export interface getStoreWorkingTimeListProps {
    wrkptnset_id: number
}
export interface getStoreWorkingTimeListResult {
    workingTimeList: WorkPattern[]
}
export const getStoreWorkingTimeList = async ({
    wrkptnset_id,
}: getStoreWorkingTimeListProps, config?: AxiosRequestConfig): Promise<getStoreWorkingTimeListResult> => {
    try {
        let params = new URLSearchParams()
        params.append("wrkptnset_id", wrkptnset_id.toString())

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/get_store_working_time_list',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
        return {
            workingTimeList: res.data.list.map( (item: any) => ObjectToWorkPattern({...item, wrkptnset_id: wrkptnset_id}) )
        }
    } catch (error) {
        throw error
    }
}

export interface setStoreWorkingTimeProps {
    wrkptnset_id: number,
    week_conditions: WeekConditionsType,
    on_all?: boolean,
    open?: Time | null,
    close?: Time | null,
}
export interface setStoreWorkingTimeResults {}
export const setStoreWorkingTime = async ({
    wrkptnset_id,
    week_conditions,
    on_all,
    open,
    close,
}: setStoreWorkingTimeProps, config?: AxiosRequestConfig): Promise<setStoreWorkingTimeResults> => {
    try {
        let params = new URLSearchParams()
        params.append("wrkptnset_id", wrkptnset_id.toString())
        params.append("week_conditions", week_conditions)
        if( on_all ) {
            params.append("on_all", on_all ? "true" : "false")
        }else{
            params.append("on_all", "false")
        }

        params.append("open", open ? open.toStringHM() : "")
        params.append("close", close ? close.toStringHM() : "")

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/set_store_working_time',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
        return {
        }
    } catch (error) {
        throw error
    }
}

export interface getStoreWorkingParticularSettingListProps {
    str_id: number
    year: number
}
export interface getStoreWorkingParticularSettingListResults {
    workingParticularSettingList: WorkingParticularSetting[]
}
export const getStoreWorkingParticularSettingList = async ({
    str_id,
    year,
}: getStoreWorkingParticularSettingListProps, config?: AxiosRequestConfig): Promise<getStoreWorkingParticularSettingListResults> => {
    try {
        let params = new URLSearchParams()
        params.append("str_id", str_id.toString())
        params.append("year", year.toString())

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/get_store_working_particular_setting_list',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
        return {
            workingParticularSettingList: res.data.list.map( (item: any) => ObjectToWorkingParticularSetting(item) )
        }
    } catch (error) {
        throw error
    }
}

export interface regStoreWorkingParticularSettingProps {
    str_id: number
    wrkptc_date: Date
    wrkptc_open?: Time
    wrkptc_close?: Time
}
export const regStoreWorkingParticularSetting = async ({
    str_id,
    wrkptc_date,
    wrkptc_open,
    wrkptc_close,
}: regStoreWorkingParticularSettingProps, config?: AxiosRequestConfig): Promise<void> => {
    try {
        let params = new URLSearchParams()
        params.append("str_id", str_id.toString());
        params.append("wrkptc_date", format(wrkptc_date, "yyyy-MM-dd"));
        params.append("wrkptc_open", wrkptc_open ? wrkptc_open.toStringHM() : "");
        params.append("wrkptc_close", wrkptc_close ? wrkptc_close.toStringHM() : "");


        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/reg_store_working_particular_setting',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
    } catch (error) {
        throw error
    }
}
export interface delStoreWorkingParticularSettingProps {
    wrkptc_id: number
}
export const delStoreWorkingParticularSetting = async ({
    wrkptc_id,
}: delStoreWorkingParticularSettingProps, config?: AxiosRequestConfig): Promise<void> => {
    try {
        let params = new URLSearchParams()
        params.append("wrkptc_id", wrkptc_id.toString());


        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/del_store_working_particular_setting',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
    } catch (error) {
        throw error
    }
}

export interface getChargeDataProps {
    chrptn_id: number
}
export interface getChargeDataResults {
    chargePattern: ChargePattern
}
export const getChargeData = async ({
    chrptn_id
}: getChargeDataProps, config?: AxiosRequestConfig): Promise<getChargeDataResults> => {
    try {
        let params = new URLSearchParams()
        params.append("chrptn_id", chrptn_id.toString());

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/get_charge_data',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
        return {
            chargePattern: ObjectToChargePattern(res.data.charge_pattern)
        }
    } catch (error) {
        throw error
    }
}

export interface getChargeListProps {
    str_id: number
    std_id: number
}
export interface getChargeListResults {
    chargePatternList: ChargePattern[]
}

export const getChargeList = async ({
    str_id,
    std_id
}: getChargeListProps, config?: AxiosRequestConfig): Promise<getChargeListResults> => {
    try {
        let params = new URLSearchParams()
        params.append("str_id", str_id.toString());
        params.append("std_id", std_id.toString());


        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/get_charge_list',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
        return {
            chargePatternList: res.data.list.map((item: any) => ObjectToChargePattern(item))
        }
    } catch (error) {
        throw error
    }
}

export interface getChargeCategoryListProps {
    access_key?: string
    offset?: number
    num?: number
}
export interface getChargeCategoryListResults {
    chargeCategoryList: ChargeCategory[]
    rows: number
}
export const getChargeCategoryList = async ({
    access_key,
    offset,
    num
}:getChargeCategoryListProps ,config?: AxiosRequestConfig): Promise<getChargeCategoryListResults> => {
    try {
        let params = new URLSearchParams()
        if( access_key !== undefined ) {
            params.append("access_key", access_key);
        }
        if( offset !== undefined ) {
            params.append("offset", offset.toString());
        }
        if( num !== undefined ) {
            params.append("num", num.toString());
        }


        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/get_charge_category_list',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
        return {
            chargeCategoryList: res.data.list.map((item: any) => ObjectToChargeCategory(item)),
            rows: parseInt(res.data.rows)
        }
    } catch (error) {
        throw error
    }
}
export interface delChargeDataProps {
    chrptn_id: number
}
export const delChargeData = async ({
    chrptn_id
}: delChargeDataProps, config?: AxiosRequestConfig): Promise<void> => {
    try {
        let params = new URLSearchParams()
        params.append("access_key", "HEAD-OFFICE_LOGIN");
        params.append("chrptn_id", chrptn_id.toString());


        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/del_charge_data',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
    } catch (error) {
        throw error
    }
}

export interface regChargeDataProps {
    chargePattern: ChargePattern
}
export interface regChargeDataResults {
    chrptn_id: number
}
export const regChargeData = async ({
    chargePattern
}: regChargeDataProps, config?: AxiosRequestConfig): Promise<regChargeDataResults> => {
    try {
        let params = new URLSearchParams()
        params.append("access_key", "HEAD-OFFICE_LOGIN");
        if (chargePattern.chrptn_id) {
            params.append("reg_data[chrptn_id]", chargePattern.chrptn_id.toString());
        }
        if (chargePattern.str_id) {
            params.append("reg_data[str_id]", chargePattern.str_id.toString());
        }
        if (chargePattern.std_id) {
            params.append("reg_data[std_id]", chargePattern.std_id.toString());
        }
        if (chargePattern.start_date) {
            params.append("reg_data[start_date]", format(chargePattern.start_date, 'yyyy-MM-dd'));
        }
        if (chargePattern.end_date) {
            params.append("reg_data[end_date]", format(chargePattern.end_date, 'yyyy-MM-dd'));
        }
        if (chargePattern.week_conditions) {
            params.append("reg_data[week]", chargePattern.week_conditions ? WeekTypesToWeekTypesNumber(chargePattern.week_conditions).toString() : "");
        }
        if (chargePattern.chrcate_id) {
            params.append("reg_data[chrcate_id]", chargePattern.chrcate_id.toString());
        }
        if (chargePattern.start) {
            params.append("reg_data[start]", chargePattern.start.toStringHM());
        }
        if (chargePattern.end) {
            params.append("reg_data[end]", chargePattern.end.toStringHM());
        }
        if (chargePattern.chrptn_charge) {
            params.append("reg_data[charge]", chargePattern.chrptn_charge.toString());
        }


        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/reg_charge_data',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }

        return {
            chrptn_id: parseInt(res.data.chrptn_id)
        }
    } catch (error) {
        throw error
    }
}

export interface getStudioPackListProps {
    str_id: number
    limit?: number
    page?: number
}
export interface getStudioPackListResults {
    studioPackList: StudioPack[]
}
export const getStudioPackList = async ({
    str_id,
    limit,
    page
}: getStudioPackListProps, config?: AxiosRequestConfig): Promise<getStudioPackListResults> => {
    try {
        let params = new URLSearchParams()
        params.append("str_id", str_id.toString());
        if (limit) {
            params.append("limit", limit.toString());
        } else {
            params.append("limit", "0");
        }
        if (page) {
            params.append("page", page.toString());
        } else {
            params.append("page", "1");
        }


        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/get_studio_pack_list',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
        return {
            studioPackList: res.data.list.map((item: any) => ObjectToStudioPack(item))
        }
    } catch (error) {
        throw error
    }
}

export interface regStudioPackDataProps {
    studioPack: StudioPack
}
export const regStudioPackData = async ({
    studioPack
}: regStudioPackDataProps, config?: AxiosRequestConfig): Promise<void> => {
    try {
        let params = new URLSearchParams()
        if( studioPack.pack_id ) {
            params.append("pack_id", studioPack.pack_id.toString());
        }
        if( studioPack.pack_str_id ) {
            params.append("str_id", studioPack.pack_str_id.toString());
        }
        if( studioPack.pack_name ) {
            params.append("pack_name", studioPack.pack_name)
        }
        if( studioPack.week_conditions ) {
            params.append("week_conditions", studioPack.week_conditions.join(","))
        }
        if( studioPack.pack_std_id ) {
            params.append("pack_std_id", studioPack.pack_std_id.toString())
        }
        if( studioPack.pack_rvtiming_start ) {
            params.append("pack_rvtiming_start", studioPack.pack_rvtiming_start.toString())
        }
        if( studioPack.pack_rvtiming_end ) {
            params.append("pack_rvtiming_end", studioPack.pack_rvtiming_end.toString())
        }
        if( studioPack.pack_rvdate_start ) {
            params.append("pack_rvdate_start", format(studioPack.pack_rvdate_start, "yyyy-MM-dd"))
        }
        if( studioPack.pack_rvdate_end ) {
            params.append("pack_rvdate_end", format(studioPack.pack_rvdate_end, "yyyy-MM-dd"))
        }
        if( studioPack.pack_rvtime_start ) {
            params.append("pack_rvtime_start", studioPack.pack_rvtime_start.toStringHM())
        }
        if( studioPack.pack_rvtime_end ) {
            params.append("pack_rvtime_end", studioPack.pack_rvtime_end.toStringHM())
        }
        if( studioPack.pack_unit_length ) {
            params.append("pack_unit_length", studioPack.pack_unit_length.toString())
        }
        if( studioPack.chrcate_id ) {
            params.append("charge_category", studioPack.chrcate_id.toString())
        }
        if( studioPack.pack_charge && studioPack.pack_charge_type ) {
            switch (studioPack.pack_charge_type) {
                case PackChargeType.discount:
                    params.append("pack_charge", `${studioPack.pack_charge}`)
                    break;
                case PackChargeType.percent:
                    params.append("pack_charge", `${studioPack.pack_charge} %`)
                    break;
                case PackChargeType.unit:
                    params.append("pack_charge", `${studioPack.pack_charge} U`)
                    break;
            }
        }
        if( studioPack.priority ) {
            params.append("priority", studioPack.priority.toString())
        }
        
        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + (studioPack.pack_id ? 'store/edit_studio_pack_data' : 'store/reg_studio_pack_data' ),
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
    } catch (error) {
        throw error
    }
}

export interface regChargeCategoryProps {
	categoryCharge: ChargeCategory;
}
export const regChargeCategory = async ({ categoryCharge }: regChargeCategoryProps, config?: AxiosRequestConfig): Promise<void> => {
	try {
		let params = new URLSearchParams();
		if (categoryCharge.chrcate_id) {
			params.append("chrcate_id", categoryCharge.chrcate_id.toString());
		}
		if (categoryCharge.chrcate_name) {
			params.append("chrcate_name", categoryCharge.chrcate_name);
		}
        params.append("member", categoryCharge.member ? "true" : "false");
		if (categoryCharge.min_number) {
			params.append("min_number", categoryCharge.min_number.toString());
		}
		if (categoryCharge.max_number) {
			params.append("max_number", categoryCharge.max_number.toString());
		}
		if (categoryCharge.rvtiming_start) {
			params.append("rvtiming_start", categoryCharge.rvtiming_start.toString());
		}
		if (categoryCharge.rvtiming_end) {
			params.append("rvtiming_end", categoryCharge.rvtiming_end.toString());
		}
		if (categoryCharge.max_unit) {
			params.append("max_unit", categoryCharge.max_unit.toString());
		}

		let res = await axios.post(process.env.REACT_APP_API_ENDPOINT + "store/reg_charge_category", params, config);
		if (res.data.status !== "SUCCESS") {
			throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status);
		}
	} catch (error) {
		throw error;
	}
};

export interface delChargeCategoryProps {
	categoryCharge: ChargeCategory;
}
export const delChargeCategory = async ({ categoryCharge }: delChargeCategoryProps, config?: AxiosRequestConfig): Promise<void> => {
	try {
		let params = new URLSearchParams();
		if (categoryCharge.chrcate_id) {
			params.append("chrcate_id", categoryCharge.chrcate_id.toString());
		}

		let res = await axios.post(process.env.REACT_APP_API_ENDPOINT + "store/del_charge_category", params, config);
		if (res.data.status !== "SUCCESS") {
			throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status);
		}
	} catch (error) {
		throw error;
	}
};